/** @prettier */
import React, { useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import { withFormik } from 'formik';
import HostedColumnsForm from './HostedColumnsForm';
import { connect } from 'react-redux';
import hostedTablesActions from 'redux/hostedTables/actions';
import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { HOSTED_TABLE_TABS } from './HostedLookupTablesList';
import { showToast } from 'utils/toast';

const HostedColumns = ({
  file,
  containsHeader,
  cancelForm,
  uploadHostedTable,
  numberOfColumn,
  uploadData,
  dataTypes,
  setStep,
  reservedColumnNames = [],
  activeTab,
  uploadConvTable,
  selectedTableData,
  isEdit,
  conversationTableNames  
}) => {
  const [columns, setColumns] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const delimiter = useSelector((state) => state.hostedTables.delimiter);
  const [reservedColNames, setReservedColNames] = useState(reservedColumnNames.map((d) => d.toLowerCase().trim()))
  const tableName = useSelector((state) => state.hostedTables.tableName);
  const tablePrefix = useSelector((state) => state.hostedTables.tablePrefix);
  const conversationTablePrefix = useSelector((state) => state.hostedTables.conversationTablePrefix);

  const shownTablePrefix = activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES ? conversationTablePrefix : tablePrefix;

  const maxColumnsCount = useSelector((state) => state.hostedTables.maxColumnsCount);

  const savedColumnTypes = useMemo(() => {
    const types = {};
    selectedTableData?.columnDescriptions?.forEach(el => {
      types[el.columnName.toLowerCase()] = el.columnType;
    });
    return types;
  }, [selectedTableData]);
  
  const getColumnType = (name) => savedColumnTypes[name.trim().replace(/\s+/g, "_").toLowerCase()] || null;

  useEffect(() => {
    if (!uploadData) {
      let columnsData = [];
      for (let i = 0; i < numberOfColumn; i++) {
        let colData = { name: '', type: null, nullable: true,  }
        if(activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES){
          colData['idColumn'] = false
        }
        columnsData.push(colData);
      }

      setColumns(columnsData);
    } else if (file) {
      Papa.parse(file, {
        header: false,
        delimiter,
        dynamicTyping: true,
        complete: function (results) {
          setCsvData(results.data);
          let columnsData = [];
          if(activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES){
            columnsData.push({name: 'CONTENT_ID', nullable: true, type: 'LONG', disabled: true })
          }
          for (let i = 0; i < results.data[0].length; i++) {
            let colData = { name: containsHeader ? results.data[0][i] : '', type: null, nullable: true };
            if(activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES){
              colData['idColumn'] = false
            };
            if(isEdit && selectedTableData){
              colData.type = getColumnType(colData.name);
            }
            columnsData.push(colData);
          };
          
          setColumns(columnsData);
        },
      });
    }
  }, [file, uploadData]);

  const validateLength = (value, type, nullable) => {
    switch (type?.dataType) {
      case 'INTEGER':
      case 'DOUBLE':
      case 'BIGINT': {
        if (nullable && (value === null || String(value).toLowerCase().trim() === 'null')) {
          return true;
        }

        if (!nullable && (value === null || String(value).trim() === '')) {
          return false;
        }

        if (isNaN(value) || value.toString().length > type.defaultLength) {
          return false;
        }

        return true;
      }
      case 'DECIMAL': {
        const decimalLength = type.defaultLength.split(',').map((val) => parseInt(val));
        if (nullable && (value === null || String(value).toLowerCase().trim() === 'null')) {
          return true;
        }

        if (!nullable && (value === null || String(value).trim() === '')) {
          return false;
        }

        if (isNaN(value)) {
          return false;
        }
        const valueArr = String(value)
          .split('.')
          .map((val) => val.trim());
        if (valueArr.length > 1 && (valueArr[0].length > decimalLength[0] || valueArr[1].length > decimalLength[1])) {
          return false;
        }
        if (valueArr[0].length > decimalLength[0]) {
          return false;
        }

        return true;
      }
      case 'VARCHAR': {
        if (nullable && (value === null || String(value).toLowerCase().trim() === 'null')) {
          return true;
        }

        if (!nullable && (value === null || String(value).trim() === '')) {
          return false;
        }

        if (String(value).length > type.defaultLength) {
          return false;
        }

        return true;
      }
      case 'BOOLEAN': {
        if (nullable && (value === null || String(value).toLowerCase().trim() === 'null')) {
          return true;
        }

        if (!nullable && (value === null || String(value).trim() === '')) {
          return false;
        }

        if (typeof value !== 'boolean') {
          return false;
        }
        return true;
      }
      default: {
        return true;
      }
    }
  };

  const validate = (values) => {
    const basicStringRegEx = activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES ?  new RegExp(/^[a-z0-9_ ]*$/i) :  new RegExp(/^[a-z0-9_]*$/i);
    const firstCharLetterRegEx = new RegExp(/^[a-z]/i);
    const errors = {};
    const duplicateValuesObj = Object.create(null);
    const csvDataClone = cloneDeep(csvData);
    if (containsHeader) {
      csvDataClone.shift();
    }
    for (let i = 0; i < values.name.length; i++) {
      if (typeof duplicateValuesObj[values.name[i].toLowerCase()] === 'undefined') {
        duplicateValuesObj[values.name[i].toLowerCase()] = 1;
      } else {
        duplicateValuesObj[values.name[i].toLowerCase()] += 1;
      }
      
      if (values.name[i] === '') {
        errors[i] = `Column ${i + 1} name is required`;
      } else if (!firstCharLetterRegEx.test(values.name[i])) {
        errors[i] = `Column ${i + 1} name must start with a letter`;
      } else if (!basicStringRegEx.test(values.name[i])) {
        errors[i] = `Column ${i + 1} name may only contain alphanumeric characters or underscore`;
      };
      if(reservedColNames.includes(values.name[i].toLowerCase()) ){
        errors[i] = `Column ${i + 1} name is reserved`;
      }
    };
    

    const duplicateValues = Object.fromEntries(Object.entries(duplicateValuesObj).filter((val) => val[1] > 1));
    for (let i = 0; i < values.name.length; i++) {
      if (values.name[i] !== '' && duplicateValues.hasOwnProperty(values.name[i].toLowerCase())) {
        errors[i] = `Column ${i + 1} must be unique`;
      };
      if(values.name[i] !== 'CONTENT_ID'){
        if (uploadData && csvDataClone.length && Object.keys(dataTypes).length > 0 && !errors[i]) {
          const nullable = typeof values.nullable[i] === 'undefined' ? true : values.nullable[i];
          const typeVal = values.type[i] || 'STRING';
          const dataType = dataTypes[typeVal];
  
          if (!csvDataClone.every((val) => validateLength(val[i], dataType, nullable))) {
            if (activeTab !== HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES) {
              if (typeVal === 'BOOLEAN') {
                errors[i] = `Column ${i + 1} must have true or false values in CSV`;
              } else {
                errors[i] = `Column ${i + 1} must have ${typeVal} values in CSV with ${dataType.defaultLength} length`;
              }
            }
          }
        }
      }
      
    }

    return errors;
  };

  const initialValues = {
    name: [],
    type: [],
    nullable: [],
    idColumn:[],
    idColumnType:"MEDIA_ID",
    idColumnName:"",
  };

  const createSchemaRequestDto = (inputSchema, tableName) => {
    return {
      schemaRequestDto : JSON.stringify({columns: inputSchema.name.map((colName, index) => ({
            columnName: colName.trim().replace(/\s+/g, "_").toLowerCase(),
            dataType: inputSchema.type[index],
            isNullable: inputSchema.nullable[index]
        })),
        tableName})
    };
}

  const FormikForm = withFormik({
    mapPropsToValues: () => initialValues,
    handleSubmit: (rawValues) => {
      if(activeTab === HOSTED_TABLE_TABS.TABLES){
      const columns = [];
      for (let i = 0; i < rawValues.name.length; i++) {
        columns.push({ columnName: rawValues.name[i], dataType: rawValues.type[i], isNullable: rawValues.nullable[i] });
      }
      uploadHostedTable(columns, delimiter);
    }else{
      let {name, type, nullable, idColumnType, idColumnName} = rawValues;
      if(name?.length > maxColumnsCount + 1){
        showToast('error', {
          task:
            'Number of Columns must not be greater than ' + maxColumnsCount,
        });
        return;
      }
      if(!idColumnName){
        showToast('error', {
          task:
            'At least one Id Column should be selected.',
        });
        return;
      };
      let tablName = isEdit ? tableName : tableName.includes(shownTablePrefix) ? tableName : shownTablePrefix+tableName;
      let tableConvNameExist = conversationTableNames.find((table) => table.tableName === tablName.toUpperCase() );
      if(!isEdit && tableConvNameExist){
        showToast('error', {
          task:
            'Table name already exist',
        });
        return;
      };
      let sendData = createSchemaRequestDto({name, type, nullable}, tablName.toUpperCase() );
      sendData = {...sendData, idColumn: idColumnName, idColumnType,  file, delimiter }
      uploadConvTable(sendData)
    }
    },
    validate,
    initialValues,
  })(HostedColumnsForm);
  return <FormikForm columns={columns} cancelForm={cancelForm} setStep={setStep} reservedColumnNames={reservedColumnNames} activeTab={activeTab}  isEdit={isEdit} />;
};

const mapStateToProps = (state) => ({
  numberOfColumn: state.hostedTables.numberOfColumn,
  uploadData: state.hostedTables.uploadData,
  file: state.hostedTables.file,
  containsHeader: state.hostedTables.containsHeader,
  dataTypes: state.hostedTables.dataTypes,
  reservedColumnNames: state.hostedTables.reservedColumnNames,
  selectedTableData: state.hostedTables.selectedTableData,
  conversationTableNames: state.hostedTables.conversationTableNames
});

const mapDispatchToProps = (dispatch) => ({
  uploadHostedTable: (columns, delimiter) => dispatch(hostedTablesActions.uploadHostedTable(columns, delimiter)),
  setStep: (step) => dispatch(hostedTablesActions.setStep(step)),
  uploadConvTable: (data) => dispatch(hostedTablesActions.uploadConvTable(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(HostedColumns);
